import React, { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation } from "swiper";
import { SRLWrapper } from "simple-react-lightbox";
import axios from "axios";
import "swiper/css";
import "swiper/css/navigation";

// Register Swiper modules
SwiperCore.use([Navigation]);

/**
 * GalleryWrapper – fully dynamic rewrite
 * ------------------------------------------------------------
 * • Fetches galleries from PHP API once on mount.
 * • Renders one Swiper carousel per gallery, with unique
 *   navigation controls generated from the gallery index.
 * • Inside each carousel, outputs every image in that gallery.
 * • Works with any number of galleries / images.
 */
const GalleryWrapper = () => {
  // ---------------------------------------------------------------------------
  // CONFIGURATION
  // ---------------------------------------------------------------------------
  const API_URL = "https://test.campfr.com/api/get_galleries/"; // ❗ update if needed
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;

  // ---------------------------------------------------------------------------
  // STATE
  // ---------------------------------------------------------------------------
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ---------------------------------------------------------------------------
  // EFFECT – Fetch once on mount
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const fetchGalleries = async () => {
      try {
        const { data } = await axios.get(API_URL, {
          headers: { "X-API-TOKEN": API_TOKEN },
        });
        setGalleries(Array.isArray(data) ? data : []);
      } catch (err) {
        setError(err.message || "Failed to fetch galleries");
      } finally {
        setLoading(false);
      }
    };

    fetchGalleries();
  }, []);

  // ---------------------------------------------------------------------------
  // SLIDER BASE CONFIG
  // ---------------------------------------------------------------------------
  const baseSliderConfig = {
    slidesPerView: 1,
    speed: 1000,
    spaceBetween: 24,
    loop: true,
    centeredSlides: true,
    roundLengths: true,
    autoplay: { delay: 9000 },
    breakpoints: {
      480: { slidesPerView: 2 },
      768: { slidesPerView: 2 },
      992: { slidesPerView: 3 },
      1200: { slidesPerView: 3 },
    },
  };

  // ---------------------------------------------------------------------------
  // UI STATES
  // ---------------------------------------------------------------------------
  if (loading) return <p>Loading galleries…</p>;
  if (error) return <p className="text-danger">Error: {error}</p>;
  if (!galleries.length) return <p>No galleries found.</p>;

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <SRLWrapper>
      <div className="gallery-wrapper">
        <div className="container">
          {galleries.map((gallery, gIndex) => {
            // Unique navigation class names per gallery
            const navPrevClass = `gallery-prev-${gIndex}`;
            const navNextClass = `gallery-next-${gIndex}`;

            // Merge base config with navigation targets
            const sliderConfig = {
              ...baseSliderConfig,
              navigation: {
                prevEl: `.${navPrevClass}`,
                nextEl: `.${navNextClass}`,
              },
            };

            // Base path for this gallery's images
            const imageBase = `${process.env.PUBLIC_URL}/images/gallery/${gallery.folder_name}`;
            // If images live elsewhere (e.g., served by the API), replace imageBase accordingly.

            return (
              <div key={gallery.id} className="gallery-group pt-110">
                {/* Header + Navigation */}
                <div className="gallery-group-header d-flex justify-content-between align-items-center">
                  <h4 className="gallery-group-title mb-0">{gallery.title}</h4>

                  <div className="gallery-arrows text-center d-lg-block d-none">
                    <div
                      className={`gallery-button-prev ${navPrevClass}`}
                      tabIndex={0}
                      role="button"
                      aria-label="Previous slide"
                    >
                      <i className="bi bi-chevron-double-left" />
                    </div>
                    <div
                      className={`gallery-button-next ${navNextClass}`}
                      tabIndex={0}
                      role="button"
                      aria-label="Next slide"
                    >
                      <i className="bi bi-chevron-double-right" />
                    </div>
                  </div>
                </div>

                {/* Slider */}
                <Swiper {...sliderConfig} className="swiper gallery-slider mt-4">
                  {gallery.images.map((img) => (
                    <SwiperSlide key={img.id}>
                      <a
                        href={`${imageBase}/${img.fullsize}`}          // file opened in the light‑box
                        data-attribute="SRL"                           // optional but explicit
                        className="gallery-item"
                      >
                        <img                                           // acts as thumbnail
                          src={`${imageBase}/thumbs/${img.thumb}`}
                          alt={`${gallery.title} – ${img.id}`}
                        />

                      </a>
                    </SwiperSlide>

                  ))}
                </Swiper>
              </div>
            );
          })}
        </div>
      </div>
    </SRLWrapper>
  );
};

export default GalleryWrapper;
